import { logoutFromMB } from '@/app/crossAppLogoutUtils';
import { AppConfig } from '@/config/appConfig';
import { isSameOriginUrl, openUrl, setToLS } from '@/lib/commonHelpers';
import {
  getAppType,
  getAppVersion,
  getDeviceInfo,
  isNewAppVersion,
} from '@/lib/offline/OfflineAppHelper';
import { offlineAppService } from '@/lib/offline/offlineAppService';
import { checkAndHandleRedirectToRegion } from '@/lib/regionUtils';
import { identifyUser } from '@/lib/telemetry/analytics';
import msalInstance from '@/pages/login/msLogin/msalInstance';
import { axiosInstance } from '@/providers/dataProvider';
import { initiateMacAppLogin, selectIsMacAppLoginActive, useAuthStore } from '@/store/authStore';
import { useCommonStore } from '@/store/commonStore';
import type { AuthProvider } from '@refinedev/core';
import * as Sentry from '@sentry/react';
import queryString from 'query-string';
import { firebaseServices } from './realtimeDbProvider/firebaseServices';

export const TOKEN_KEY = 'ap-token';
const MICROSOFT_LOGIN_AUTHORITY_BASE = 'https://login.microsoftonline.com';
const LOGIN_ROUTE = '/auth/login';

type CurrentUser = {
  id: string;
  name: string;
  email: string;
  role: string;
  org?: any;
};

type AuthResponse = {
  token: string;
  user?: any;
  extra_attrs?: any;
};

// Sets Sentry data for the running app after login (APL-11729):
//   1. React-side app-type tag + device-info context for any native app type
//      (moved here from the CheckAndSetAppType component's mount effect).
//   2. The native-app Sentry user, forwarded to the mac/ios shell on
//      supported app versions.
const setAppSentryUserAndTags = (currentUser: CurrentUser): void => {
  const appType = getAppType();
  const appVersion = getAppVersion(appType);
  try {
    if (appType !== 'web') {
      Sentry.setTag('AssessPrepAppType', appType);
      Sentry.setContext('App device info', getDeviceInfo(appType));
    }

    if (
      (appType === 'mac' && isNewAppVersion(appVersion, '11.0.0')) ||
      (appType === 'ios' && isNewAppVersion(appVersion, '5.7.0'))
    ) {
      const sentryUserObj = {
        id: currentUser.id,
        username: currentUser.name,
        email: currentUser.email,
        role: currentUser.role,
        org: currentUser.org?.name,
      };
      const sentryUserStr = JSON.stringify(sentryUserObj);
      offlineAppService.executeAppMethod({
        key: 'setSentryUser',
        value: sentryUserStr,
      });
    }
  } catch (error) {
    Sentry.captureException(error);
    console.log('setAppSentryUserAndTags error');
  }
};

// Helper Functions
const setSentryUser = (currentUser: CurrentUser): void => {
  Sentry.setUser({
    id: currentUser.id,
    username: currentUser.name,
    email: currentUser.email,
    role: currentUser.role,
    org: currentUser.org?.name,
  });
  setAppSentryUserAndTags(currentUser);
};

export const checkAndHandleRedirect = (responseData: AuthResponse): boolean => {
  const userAttributes = responseData.user?.data?.attributes;
  const enabledFeatures = userAttributes?.org?.enabled_features ?? {};
  const isSuperAdminOrSupport =
    userAttributes?.role === 'superadmin' || userAttributes?.role === 'support';
  const isEmbedded = window.self !== window.top && !userAttributes?.org?.name.includes('21K');
  const isDevOrDemoMode = AppConfig.mode === 'development';
  const isGuest = userAttributes?.custom_fields?.account_type === 'guest';

  // if (isSuperAdminOrSupport && !enabledFeatures.redirect_to_v3) {
  //   message.error('V3 is not yet enabled for superadmin organization');
  //   useAuthStore.getState().logout();
  // }
  // For guest login checking from store for redirect to v3
  // const isRedirectToV3 = useAuthStore.getState().isRedirectToV3;

  // For Export to pdf we use superadmin token which result in redirection to v2 web url
  if (
    !enabledFeatures.turn_off_redirection &&
    !enabledFeatures.redirect_to_v3 &&
    !isSuperAdminOrSupport &&
    !isDevOrDemoMode &&
    !isGuest &&
    !isEmbedded
  ) {
    // If we logout v3 when v2 logout clicked then it will add extra delay for all users to logout from v2 which may affect the user experience for all
    useAuthStore.getState().logout();
    const token = responseData.token;
    let redirectUrl = '';

    const currentUrl = window.location.href.replace('https://v3-', 'https://');
    if (currentUrl.includes('/assessments/')) {
      redirectUrl = `${currentUrl
        .replace('/assessments/', '/e/tests/')
        .replace('/assessments', '/u/tests')}`;
    } else {
      redirectUrl = `${AppConfig.v2WebUrl}`;
    }

    window.location.href = redirectUrl.includes('?')
      ? `${redirectUrl}&token=${token}`
      : `${redirectUrl}?token=${token}`;
  }

  return false;
};

const setAuthState = (
  responseData: AuthResponse,
  partnerName?: string | null,
  jwtToken?: string
): void => {
  const userAttributes = responseData.user?.data?.attributes;

  // For guest login first time setting to store
  // const isRedirectToV3 = responseData.extra_attrs?.exp_org_enabled_features?.redirect_to_v3;

  const authState: any = {
    token: responseData.token,
    user: userAttributes,
    isAuthenticated: true,
    loading: false,
    error: null,
  };

  // if (isRedirectToV3 !== undefined) {
  //   authState.isRedirectToV3 = isRedirectToV3;
  // }

  useAuthStore.getState().setAuthState(authState);

  // Set common resource state in the common resource store
  if (partnerName || jwtToken) {
    useCommonStore.getState().setCommonState({
      ...(partnerName && { partnerName }),
      ...(jwtToken && { jwtToken }),
    });
  }

  if (userAttributes) {
    setSentryUser(userAttributes);
    identifyUser(userAttributes.id?.toString(), {
      role: userAttributes.role,
      org_id: userAttributes.org?.id,
    });
  }
};

export type HandleAuthSuccessOptions = {
  /** Partner name for cross-app auth (e.g. 'mb' for ManageBac). */
  partnerName?: string | null;
  /** Refine SPA redirect target — in-app navigation after auth. */
  redirectTo?: string;
  /** JWT token for partner-auth flows; persisted in commonStore. */
  jwtToken?: string;
  /**
   * Explicit URL to navigate to with a full-page reload. Opaque —
   * caller owns the URL construction (origin, query string, any
   * flags the destination needs). Used in two contexts:
   *   - Mac-app PKCE bridge → forwarded as `&landing_url=<encoded>` to
   *     `verify_app`. BE forwards through the 302 chain, Mac app
   *     preserves it when reloading FE, check() lands on it.
   *   - Web context with no Mac-app session → handleAuthSuccess
   *     navigates to the URL's path+search+hash on the CURRENT origin
   *     (the session just written lives here; an absolute hop to
   *     AppConfig.webUrl would drop it when the two hosts diverge).
   * Set this when you need a full page reload to a known URL;
   * leave it unset to let the Refine `redirectTo` path handle the
   * in-SPA navigation.
   *
   * Name is intentionally distinct from OAuth-standard `redirect_uri`
   * (the callback URL concept) — this is "where to land after login
   * completes". The two were conflated previously and confused
   * callers tracing the flow.
   */
  landing_url?: string;
};

export const handleAuthSuccess = (
  responseData: AuthResponse,
  options: HandleAuthSuccessOptions = {}
) => {
  const { partnerName, redirectTo, jwtToken } = options;
  // Open-redirect guard: drop any non-trusted-origin landing_url before
  // it reaches the Mac-app bridge URL (forwarded through BE → Mac app
  // → FE check() → window.location.href) or the web-context direct
  // nav below. Both sinks treat landing_url as already-validated.
  // Two trusted anchors: the current serving origin AND AppConfig.webUrl
  // (the domain callers like GuestJoinCode build landing_url against) —
  // they diverge in local dev / PR-session envs where VITE_WEB_URL points
  // at the canonical deploy host while the page is served elsewhere;
  // anchoring on window.location.origin alone silently dropped every
  // guest-join landing_url there (R13b app-bug). evil.com still fails
  // both checks; parse errors still fail closed.
  let landing_url = options.landing_url;
  if (
    landing_url &&
    !isSameOriginUrl(landing_url) &&
    !isSameOriginUrl(landing_url, AppConfig.webUrl)
  ) {
    apConsole.warn('[handleAuthSuccess] rejecting non-same-origin landing_url', landing_url);
    landing_url = undefined;
  }

  // Expired Mac-app intent — macAppLoginAt was armed but its TTL
  // elapsed before login completed. Abort instead of silently falling
  // through to a web session the user didn't ask for.
  const authState = useAuthStore.getState();
  if (authState.macAppLoginAt && !selectIsMacAppLoginActive(authState)) {
    apConsole.warn('[macAppLogin] expired before login completed → /auth/callback?error');
    useAuthStore.getState().logout();
    useAuthStore.getState().setAuthState({ macAppLoginAt: null });
    void useAuthStore
      .getState()
      .flushToPersistence()
      .then(() => {
        window.location.href = `${AppConfig.webUrl}/auth/callback?error=mac_app_session_expired`;
      })
      .catch((err) => {
        Sentry.captureException(err);
      });
    return { success: false, redirectTo: false } as unknown as { success: true };
  }

  // Mac-app PKCE bridge — MUST run before setAuthState. The
  // ASWebAuthenticationSession (ASW) "private window" the Mac app
  // opens to handle login is a transient, one-shot login surface:
  //
  //   - The Mac app receives the JWT synchronously from
  //     /api/v1/auth/native_token's JSON response (BE-controlled).
  //     The ASW window does not need the JWT in its store to hand
  //     off the user to the Mac app.
  //   - On successful AASA dispatch, Safari dismisses the ASW
  //     window — anything in store / localforage is thrown away.
  //
  // If we wrote auth state here, an AASA-dispatch failure
  // (Mac app not installed, outdated entitlements, dispatch lost
  // mid-handoff, network blip) would leave the ASW window stuck
  // at /auth/callback with token + user + isAuthenticated:true in
  // localforage. The user could then edit the URL bar and explore
  // the account inside the private window — a usable session that
  // survives until the window dismisses on its own. We avoid this
  // entire class of bug by resetting authStore to its initial
  // state and flushing BEFORE the bridge nav.
  //
  // Top-level GET is required: cross-origin XHR from app.* to api.*
  // doesn't carry the session cookie. Mac-app intent wins over the
  // v2/v3 routing handled below.
  //
  // Applies to every login type — password, SSO providers, OTP, and
  // Guest Join Code. Callers that need to land on a specific URL
  // post-PKCE (e.g. GuestJoinCode → experience page) pass landing_url;
  // it's appended to the verify_app URL as &landing_url=<encoded>, BE
  // forwards it through the 302 chain, Mac app preserves it when
  // reloading FE, and check() below redirects to it once the token
  // is set. Callers that perform their own post-login navigation
  // must skip that nav in Mac-app context so the bridge nav isn't
  // overwritten.
  if (selectIsMacAppLoginActive(useAuthStore.getState())) {
    let bridgeUrl = `${AppConfig.apiUrl}auth/verify_app?token=${encodeURIComponent(
      responseData.token
    )}`;
    if (landing_url) {
      bridgeUrl += `&landing_url=${encodeURIComponent(landing_url)}`;
    }
    apConsole.log('[macAppLogin] active → top-level nav to verify_app (no state persist)', {
      landing_url,
    });
    // Reset authStore + explicitly clear macAppLoginAt. logout() preserves
    // macAppLoginAt when active (user-initiated-logout semantic), but the
    // bridge flow needs a HARD clear — otherwise the Mac app's post-PKCE
    // FE reload would re-fire this same bridge branch and dead-end at
    // BE's missing_code_challenge.
    useAuthStore.getState().logout();
    useAuthStore.getState().setAuthState({ macAppLoginAt: null });
    void useAuthStore
      .getState()
      .flushToPersistence()
      .then(() => {
        window.location.href = bridgeUrl;
      })
      .catch((err) => {
        Sentry.captureException(err);
      });
    return { success: true, redirectTo: false } as unknown as { success: true };
  }

  // Non-Mac-app context — write auth state and continue with post-login
  // routing.
  setAuthState(responseData, partnerName, jwtToken);

  // Explicit landing_url in web context — navigate directly. Used by
  // Guest Join Code which builds `${webUrl}/assessments/<uuid>?firstLoad=true`
  // (no token — it travels via authStore). The destination URL has no
  // ?token=, so the experience page is entirely store-dependent —
  // flushing before nav is mandatory, not optional.
  // Navigate to the PATH ONLY (pathname + search + hash): the auth state
  // just written lives on THIS origin, and the validated landing_url may
  // be absolute to AppConfig.webUrl (a different host in dev/PR-session
  // envs) — leaving the serving origin would lose the fresh session and
  // bounce to /auth/login. Same-origin by construction. (The Mac-app
  // bridge branch above deliberately keeps the absolute URL — the PKCE
  // chain forwards it across origins.)
  if (landing_url) {
    const parsed = new URL(landing_url, window.location.origin);
    const landingPath = parsed.pathname + parsed.search + parsed.hash;
    apConsole.log('[handleAuthSuccess] explicit landing_url → nav', landingPath);
    void useAuthStore
      .getState()
      .flushToPersistence()
      .then(() => {
        window.location.href = landingPath;
      })
      .catch((err) => {
        Sentry.captureException(err);
      });
    return { success: true, redirectTo: false } as unknown as { success: true };
  }

  checkAndHandleRedirect(responseData);

  return { success: true, redirectTo };
};

// Tear down auth state ONLY when something is actually held. logout() clears
// the react-query cache, which removes the mounted <Authenticated> check query
// and forces it to refetch — an unconditional logout inside check() re-cleared
// the cache on every refetch and looped the auth gate's loading skeleton
// forever (staging stuck-at-'/' after a 401).
const logoutIfAuthStateHeld = (): void => {
  const state = useAuthStore.getState();
  if (state.token || state.isAuthenticated || state.user) {
    void state.logout();
  }
};

const handleAuthError = (error: any, context: string, options?: { redirectTo?: string }) => {
  apConsole.error(`${context} error:`, error);
  //
  const statusCode = error?.response?.status;
  const responseData = error?.response?.data;
  // Handle 404 errors for authentication endpoints (sessions.json, get_user)
  // These indicate user not found or session not found scenarios
  const requestUrl = error.config?.url;
  // Check if the error is a 404 on authentication endpoints (session.json, get_user.json o)
  const isAuthenticationNotFoundError =
    (requestUrl?.includes('sessions.json') || requestUrl?.includes('get_user')) &&
    [404].includes(statusCode);

  if ([401].includes(statusCode) || isAuthenticationNotFoundError) {
    const errorMessage = responseData?.error
      ? responseData.error
      : 'Access denied. Please verify your credentials and try again.';
    logoutIfAuthStateHeld();
    // redirectTo only from check()/onError callers: Refine's useLogin
    // navigates on redirectTo even when success:false, which would strip
    // the ?to= return target from a failed login attempt.
    return Promise.resolve({
      authenticated: false,
      success: false,
      ...(options?.redirectTo ? { redirectTo: options.redirectTo } : {}),
      error: {
        name: 'Unauthorized',
        message: errorMessage,
        status: statusCode,
        code: 'AUTH_401',
      },
    });
  } else {
    console.log(
      'Capturing error to Sentry for failed APIs other than sessions and get_user',
      error
    );
    Sentry.captureException(error);
    if (error.config?.url?.includes('get_user')) {
      return Promise.resolve({
        success: false,
        redirectTo: '/error',
        authenticated: false,
      });
    } else {
      return Promise.resolve({
        success: false,
        error: error,
        // In case of sessions error, we want to logout the user
        authenticated: !error.config?.url?.includes('sessions'),
      });
    }
  }
};

const makeAuthenticatedRequest = async (
  endpoint: string,
  token?: string,
  params?: Record<string, string | undefined>
) => {
  const headers = token ? { token } : {};
  return await axiosInstance.get(`${AppConfig.apiUrl}${endpoint}`, { headers, params });
};

const makeAuthenticatedRequestWithJwt = async (endpoint: string, jwt: string, payload: any) => {
  const headers = { jwt };
  return await axiosInstance.post(`${AppConfig.apiUrl}${endpoint}`, payload, { headers });
};
// Authentication Provider
export const authProvider: AuthProvider = {
  login: async ({ username, email, password, responseObj, resource, payload }) => {
    apConsole.log('Login payload:', responseObj);

    // OTP Login
    if (resource === 'otp') {
      try {
        const response = await axiosInstance.post(
          `${AppConfig.apiUrl}/sessions/verify_otp`,
          payload
        );

        apConsole.log('OTP login response:', response);
        return handleAuthSuccess(response.data, { redirectTo: '/' });
      } catch (error) {
        return handleAuthError(error, 'OTP authentication');
      }
    }

    // Social Login (Google/Microsoft)
    if ((resource === 'google' || resource === 'microsoft') && responseObj) {
      const loginObj =
        resource === 'google'
          ? { googleLoginObj: { profileObj: {}, tokenId: responseObj } }
          : { msLoginObj: responseObj };

      // try {
      //   if (resource === 'microsoft' && responseObj?.account) {
      //     const msalObj = await msalInstance.getInstance();
      //     msalObj.setActiveAccount(responseObj?.account);
      //   }
      // } catch (error) {
      //   apConsole.error('Error during MSAL setActiveAccount:', error);
      // }

      try {
        const response = await axiosInstance.post(`${AppConfig.apiUrl}/sessions.json`, loginObj);
        return handleAuthSuccess(response.data, { redirectTo: '/' });
      } catch (error) {
        return handleAuthError(error, `${resource} authentication`);
      }
    }

    // Email/Password Login
    if ((username || email) && password) {
      try {
        const finalUsername = username || email;

        const response = await axiosInstance.post(`${AppConfig.apiUrl}/sessions.json`, {
          user: {
            email: finalUsername,
            password,
          },
        });

        apConsole.log('Login response:', response);

        return handleAuthSuccess(response.data, { redirectTo: '/' });
      } catch (error) {
        return handleAuthError(error, 'Email/password authentication');
      }
    }

    return Promise.reject(new Error('Invalid username or password'));
  },

  logout: async (options: any) => {
    // const { preserveOriginalUserRole } = options;
    apConsole.log('Logging out user');

    const appType = getAppType();
    const authState = useAuthStore.getState();
    const appRegion = AppConfig.region;
    const user = authState.user;
    const userRole = user?.role;
    const lastSignInFrom = user?.custom_fields?.last_sign_in_from;
    const isMb = user && user.org && user.org.is_mb;
    // const browserName = getBrowserName();
    const enabledFeatures = user?.org?.enabled_features;
    // Try to logout from MSAL
    // const shouldLogoutFromMSAL =
    //   enabledFeatures?.logout_from_msal && appType === 'cros' && lastSignInFrom === 'microsoft';
    const shouldLogoutFromMSAL =
      enabledFeatures?.logout_from_msal && userRole === 'student' && lastSignInFrom === 'microsoft';

    // const shouldRedirectToV2AndLogout =
    //   appType === 'mac' || appType === 'ios' || browserName === 'Safari' || appType === 'cros';

    // NOTE: When v3 Released for all schools then we should do verify all logout logic in v3 for all login cases instead of v2
    const shouldRedirectToV2AndLogout = false;

    // const shouldOpenIFrameAndLogoutFromV2 = !(
    //   AppConfig.mode === 'development' || shouldRedirectToV2AndLogout
    // );

    // if (shouldOpenIFrameAndLogoutFromV2) {
    //   try {
    //     await broadcastLogoutToV2();
    //     console.log('V2 logout completed');
    //   } catch (error) {
    //     throw new Error('Error during V2 iframe logout:', error as Error);
    //   }
    // }

    if (appRegion !== 'china') {
      // Logout from firebase. This is the single app-level Firebase teardown
      // point — effect cleanups no longer sign out (see contexts/realTime).
      await firebaseServices?.firebaseSignout();
      // TODO: add supabase sign out
    }

    // if (!preserveOriginalUserRole) {
    //   const originalUserRole = authState.originalUserRole;
    //   if (originalUserRole != null) {
    //     authState.setAuthState({ originalUserRole: null });
    //   }
    // }

    await authState.logout();

    if (shouldRedirectToV2AndLogout) {
      // Handle v2 logout with open new browser window
      // Doing if else because openUrl is async
      let url = `${AppConfig.v2WebUrl}/cross-app-logout`;
      if (shouldLogoutFromMSAL) {
        url += `?shouldLogoutInV2FromMSAL=${shouldLogoutFromMSAL}`;
      }
      openUrl(url);
    } else {
      if (isMb) {
        // Logout from MB first
        await logoutFromMB();
      }

      if (shouldLogoutFromMSAL) {
        try {
          const msalObj = await msalInstance.getInstance();
          // const account = msalObj.getActiveAccount() || msalObj.getAllAccounts()[0];
          // if (account) {
          // await msalObj.logoutRedirect({
          //     account: account, // TODO: not working
          //     postLogoutRedirectUri: `${AppConfig.webUrl}/auth/login`,
          //   });
          // }
          const accounts = msalObj.getAllAccounts();
          if (accounts.length > 0) {
            // Find the logged-in account (by email or preferred_username)
            const targetAccount =
              accounts.find(
                (account: any) =>
                  account.idTokenClaims?.email === user?.email ||
                  account.idTokenClaims?.preferred_username === user?.email
              ) ?? accounts[0];

            const logoutRequest: any = {
              account: targetAccount,
              postLogoutRedirectUri: `${AppConfig.webUrl}/auth/login`,
            };

            // Only add authority if tenantId exists to prevent malformed URL
            // Use the account's specific tenant for logout
            if (targetAccount.tenantId) {
              logoutRequest.authority = `${MICROSOFT_LOGIN_AUTHORITY_BASE}/${targetAccount.tenantId}/`;
            }

            await msalObj.logoutRedirect(logoutRequest);
          }
        } catch (error) {
          throw new Error('Error during MSAL logout:', error as Error);
        }
      }

      /**
       * Redirect to login page after logout completion.
       *
       * Note: Refine's logout method automatically stores the redirectTo value in URL query parameters.
       * To avoid unwanted query parameters in the login URL, we use openUrl() to perform a clean
       * redirect to '/auth/login' instead of relying on Refine's built-in redirect mechanism.
       *
       * This ensures users land on a clean login page without any residual query parameters
       * that could interfere with the login flow or cause confusion.
       */
      openUrl('/auth/login');
    }

    return {
      success: true,
      redirectTo: '/',
    };
  },
  check: async () => {
    const queryParams = queryString.parse(location.search);
    apConsole.log('Check query params:', queryParams);
    const urlToken = queryParams.token as string;
    const jwtToken = queryParams.jwt as string;
    const appTypeFromQuery = queryParams.appType as string;
    const originalUserRoleFromQuery = queryParams.originalUserRole as string;
    if (appTypeFromQuery && appTypeFromQuery === 'cros') {
      setToLS('appType', appTypeFromQuery);
    }

    if (originalUserRoleFromQuery) {
      useAuthStore.getState().setAuthState({ originalUserRole: originalUserRoleFromQuery });
    }

    // Arm macAppLoginAt + strip ?from_mac_app=1 from URL when present.
    // Self-gating no-op when the flag isn't there. Runs early to cover
    // the case where Refine fires check() before LoginPage mounts.
    initiateMacAppLogin();

    // ?landing_url= post-login redirect. After PKCE (or any cross-origin
    // login) the URL may carry ?landing_url=<url> indicating where the
    // user should land. Same-origin guard rejects cross-origin /
    // custom-scheme values (open-redirect protection). Type guard
    // coerces away array / null shapes that query-string can produce
    // for repeated keys.
    //
    // Name is intentionally distinct from OAuth-standard `redirect_uri`
    // — this is "where to land after login completes", not the OAuth
    // callback URL concept.
    const landingUrlParam =
      typeof queryParams.landing_url === 'string' ? queryParams.landing_url : undefined;
    const effectiveToken = urlToken || useAuthStore.getState().token;
    if (landingUrlParam && effectiveToken && isSameOriginUrl(landingUrlParam)) {
      apConsole.log('[check] ?landing_url= → redirecting', landingUrlParam);
      // The destination URL is typically path-only (caller's
      // landing_url carries no token), so authStore is the only
      // carrier for the destination page. Persist the URL token (if
      // present) and AWAIT the flush before the top-level nav —
      // otherwise the destination page rehydrates with no token and
      // bounces to /auth/login.
      if (urlToken) {
        useAuthStore.getState().setAuthState({ token: urlToken });
      }
      await useAuthStore.getState().flushToPersistence();
      window.location.href = landingUrlParam;
      return { authenticated: true };
    }

    // client and expUUID cases not handled here.

    // Handle token from URL query parameters
    if (urlToken) {
      const currentUrl = window.location.href;
      try {
        useAuthStore.getState().setAuthState({
          token: urlToken,
        });
        const response = await makeAuthenticatedRequest('/get_user.json');
        apConsole.log('URL token check:', response);
        const enabledFeatures = response.data.user?.data?.attributes?.org?.enabled_features;
        const appType = getAppType();
        // If validate_seb_config is enabled and appType is seb, set queryParamsToken and initialUrlWithToken
        const sebValidationData = appType === 'seb' && {
          queryParamsToken: urlToken,
          initialUrlWithToken: currentUrl,
        };

        useAuthStore.getState().setAuthState({ shouldCleanAuthParams: true, ...sebValidationData });
        if (response.data.token) {
          await handleAuthSuccess(response.data, {
            partnerName: queryParams.partner as string | null,
          });
          return Promise.resolve({ authenticated: true });
        } else {
          logoutIfAuthStateHeld();
          return Promise.resolve({ authenticated: false, redirectTo: LOGIN_ROUTE });
        }
      } catch (error) {
        apConsole.error('URL token validation failed:', error);
      }
    }
    await useAuthStore.getState().waitForHydration();

    if (jwtToken) {
      const partnerName: any = queryParams.partner ?? 'mb';
      const uuidRegex = /\/(?:assessments|tests)\/([A-Z0-9]+)/;
      const match = location.pathname.match(uuidRegex);
      const expUUID = match ? match[1] : null;
      const payload: any = {
        jwt: jwtToken,
        client: partnerName,
        partner: partnerName,
      };
      if (expUUID) {
        payload.exp_uuid = expUUID;
      }
      try {
        const response = await makeAuthenticatedRequestWithJwt(
          '/verify_jwt.json',
          jwtToken,
          payload
        );

        // apConsole.log(history.replaceState, 'URL jwt check:', response);

        if (response?.data?.redirect_to_exp_uuid) {
          console.log(
            'check ===> double-render-check redirect_to_exp_uuid',
            response?.data?.redirect_to_exp_uuid
          );
          // special case for redirecting to new exp - used in pamoja bank test link case
          // Only setting token in auth store without setting isAuthenticated - after redirection, checkAuthState will be called and set token and other params in state as default
          // Directly logging in user and then redirecting to new exp was causing double render issue - isAuthenticated was being set to true before redirect trigger and was showing 1st test and then redirecting to new test
          useAuthStore.getState().setAuthState({ token: response.data.token });
          const newUUID = response.data.redirect_to_exp_uuid;
          const currentURL = window.location.href;
          let updatedURL = currentURL;
          if (
            /\/assessments\/[^/]+/.test(currentURL) ||
            /\/e\/tests\/[^/]+/.test(currentURL) ||
            /\/u\/tests\/[^/]+/.test(currentURL)
          ) {
            updatedURL = currentURL.replace(
              /\/(?:assessments|e\/tests|u\/tests)\/[^/]+/,
              `/assessments/${newUUID}`
            );
          }
          // console.log("verifyJwt redirecting to new exp", newUUID, currentURL, updatedURL);

          console.log('double-render-check redirecting now');
          window.location.href = updatedURL;
        } else if (response.data.token) {
          await handleAuthSuccess(response.data, { partnerName, jwtToken });
          useAuthStore.getState().setAuthState({ shouldCleanAuthParams: true });
          return Promise.resolve({ authenticated: true });
        } else {
          logoutIfAuthStateHeld();
          useAuthStore.getState().setAuthState({ shouldCleanAuthParams: true });
          return Promise.resolve({ authenticated: false, redirectTo: LOGIN_ROUTE });
        }
      } catch (error) {
        apConsole.error('check ===> URL jwt validation failed:', error);
        useAuthStore.getState().setAuthState({ shouldCleanAuthParams: true });
      }
    }

    // Handle existing token from store
    const token = useAuthStore.getState().token;
    apConsole.log('Checking stored token:', !!token);
    if (token) {
      const maxRetries = 2;
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const response = await makeAuthenticatedRequest('/get_user.json');

          const partnerName = window.name == '21k-assessprep-embed' ? '21k' : null;
          useCommonStore.getState().setPartnerName(partnerName);

          if (response.data.token) {
            await handleAuthSuccess(response.data);
            return Promise.resolve({ authenticated: true });
          } else {
            // 200 with no token = not authenticated. This shape used to fall
            // into handleAuthError's else-branch and come back
            // authenticated:true (no statusCode, no config.url) — a dead-end.
            apConsole.error('Authentication check failed: get_user returned no token');
            logoutIfAuthStateHeld();
            return Promise.resolve({
              authenticated: false,
              success: false,
              redirectTo: LOGIN_ROUTE,
            });
          }
        } catch (error: any) {
          //get this error when User reloads / navigates away
          const isErrorNetworkRequest = error?.code === 'ERR_NETWORK';
          if (isErrorNetworkRequest && attempt < maxRetries) {
            await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
            continue;
          }
          return handleAuthError(error, 'Authentication check failed', {
            redirectTo: LOGIN_ROUTE,
          });
        }
      }
    }

    // Check region redirect before showing login page (prevents flash)
    if (checkAndHandleRedirectToRegion()) {
      return Promise.resolve({ authenticated: false });
    }

    // Guarded logout (see logoutIfAuthStateHeld) + the LOGIN route, never '/'
    // — '/' is itself auth-gated, so redirecting there self-loops.
    logoutIfAuthStateHeld();
    return Promise.resolve({
      authenticated: false,
      redirectTo: LOGIN_ROUTE,
    });
  },

  getPermissions: async () => {
    try {
      // Placeholder for permissions logic
      const permissionsData = {};
      return permissionsData;
    } catch (error: any) {
      apConsole.error('Get permissions error:', error);
      return;
    }
  },

  getIdentity: async () => {
    const token = useAuthStore.getState()?.token;
    const user = useAuthStore.getState().user;

    if (!token) {
      return Promise.reject(new Error('User token not available'));
    }

    try {
      apConsole.log('Getting user identity');
      if (user) {
        return Promise.resolve(user);
      }

      apConsole.error('Identity token not found');
      useAuthStore.getState().logout();
      return null;
    } catch (error) {
      apConsole.error('Get identity error:', error);
      useAuthStore.getState().logout();
      return null;
    }
  },

  updatePassword: async ({ password, confirmPassword, reset_token }) => {
    apConsole.log('Updating password');
    const errorMessage = 'Failed to update password';

    try {
      const response = await axiosInstance.put(`${AppConfig.apiUrl}passwords.json`, {
        user: {
          password,
          confirmPassword,
          reset_token,
        },
      });

      if (response.status === 200) {
        return {
          success: true,
          redirectTo: '/',
          successNotification: {
            message: 'Password updated',
            description: 'Password updated successfully',
          },
        };
      }

      return {
        success: false,
        error: new Error(response?.data?.message || errorMessage),
      };
    } catch (error: any) {
      return {
        success: false,
        error: new Error(error?.response?.data?.message || errorMessage),
      };
    }
  },

  onError: async (error) => {
    apConsole.error('Auth provider error:', error);

    /**
     * Handles authentication errors from Refine hooks (useShow, useList, useCreate, useUpdate, useDelete)
     * If a 401 Unauthorized error occurs, logs out the user and returns an error object
     * This ensures proper handling of expired/invalid authentication tokens
     */

    return handleAuthError(error, 'Auth provider error', { redirectTo: LOGIN_ROUTE });
  },

  forgotPassword: async ({ email }) => {
    const trimmedEmail = email.toLowerCase().trim();

    const forgotPasswordData = {
      user: {
        email: trimmedEmail,
      },
    };

    try {
      await axiosInstance.post(`${AppConfig.apiUrl}passwords.json`, forgotPasswordData);
      return {
        success: true,
        successNotification: {
          message: 'Reset link sent',
          description: 'Password reset link sent. Please check your email',
        },
      };
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        'An unexpected error occurred. Please try again.';
      return {
        success: false,
        error: new Error(errorMessage),
      };
    }
  },
};

/**
 * Fetches fresh user identity from API and updates the store.
 * Use this when you need to refresh user data after mutations.
 * @returns The updated user attributes or null if failed
 */
export const refetchIdentity = async () => {
  const token = useAuthStore.getState()?.token;

  if (!token) {
    apConsole.error('Cannot refetch identity: No token available');
    return null;
  }

  try {
    apConsole.log('Refetching user identity from API');
    const response = await makeAuthenticatedRequest('/get_user.json');

    if (response.data?.user?.data?.attributes) {
      const userAttributes = response.data.user.data.attributes;
      // Update store with fresh user data
      useAuthStore.getState().setAuthState({ user: userAttributes });
      return userAttributes;
    }

    apConsole.error('Identity not found in response');
    return null;
  } catch (error) {
    apConsole.error('Refetch identity error:', error);
    return null;
  }
};
